<script setup lang="ts">
import { IconEye } from '@tabler/icons-vue'
import { computed, ref } from 'vue'

import BaseIcon from '../BaseIcon.vue'

import { EventsToValidate, useComputedAttrs, useComputedInput, useEventsToValidate } from './helpers'
import { TextualInputProps } from './types'

const props = defineProps<TextualInputProps>()
const emit = defineEmits(['update:modelValue', 'change', 'blur', 'validate', 'invalid'])
const input = useComputedInput(props, emit)
const attrs = useComputedAttrs(props, 'passwordViewable')
const events = useEventsToValidate(emit)
const passwordViewableType = ref<'password' | 'text'>('password')

defineOptions({
  inheritAttrs: false,
})

const togglePasswordField = () => {
  passwordViewableType.value = passwordViewableType.value === 'password' ? 'text' : 'password'
}

const toggleDisabled = computed(() => Boolean(props.disabled || props.readOnly))
const toggleAriaLabel = computed(() => (passwordViewableType.value === 'password' ? 'Show password' : 'Hide password'))

for (const key of Object.keys(events)) {
  events[key as EventsToValidate] = () => {
    passwordViewableType.value = 'password'
    // @ts-ignore
    emit(key as EventsToValidate)
  }
}
</script>

<template>
  <input
    ref="inputEl"
    v-model="input"
    v-autofocus="props.autofocus"
    v-bind="attrs"
    class="input"
    :type="passwordViewableType"
    :aria-required="props.required"
    :aria-invalid="props.invalid"
    :aria-describedby="props.id"
    v-on="events"
    :tabindex="props.tabindex"
  />
  <button
    type="button"
    class="toggle-icon"
    :aria-label="toggleAriaLabel"
    :aria-pressed="passwordViewableType === 'text'"
    :disabled="toggleDisabled"
    @click="togglePasswordField"
  >
    <BaseIcon class="toggle-base-icon" :icon="IconEye" />
  </button>
</template>

<style scoped>
.toggle-icon {
  --_icon-color: var(--c-input-icon-color);
  position: absolute;
  top: 50%;
  right: 16px;
  margin-top: -15px;
  width: 24px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  padding-left: 8px;
  color: var(--_icon-color);
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.toggle-base-icon {
  font-size: 16px;
}
</style>
